import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Trophy, Plus, Check, X, ArrowLeft, Send, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AchievementsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedBrand, setSelectedBrand] = useState(null); // drill-in
  const [perf, setPerf] = useState(null);
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [targetDialog, setTargetDialog] = useState({ open: false, employee_id: '', target: '' });
  const [entryDialog, setEntryDialog] = useState({ open: false, achieved: '', date: new Date().toISOString().slice(0, 10), product_brand: '', notes: '' });
  const [brandsMaster, setBrandsMaster] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');

  const isSuperAdmin = user?.system_role === 'super_admin';
  const isManagement = user?.system_role === 'management';
  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  // Anyone (any job role) can submit their own brand achievement if they have a brand mapped.
  const canSubmitOwnEntry = !!user;
  const canSetTargets = isSuperAdmin || isManagement;

  const fetchData = useCallback(async () => {
    setEntries([]);
    try {
      const [pRes, aRes, eRes, bRes] = await Promise.all([
        isManager ? api.get('/brand-performance', { params: { month } }) : Promise.resolve({ data: null }),
        api.get('/achievements'),
        canSetTargets ? api.get('/employees').then(r => ({ data: r.data })) : Promise.resolve({ data: [] }),
        api.get('/brand-list'),
      ]);
      setPerf(pRes.data);
      // Month filter applies to approved/rejected entries; pending entries always shown (must be actioned).
      setEntries((aRes.data || []).filter(a => a.status === 'pending' || (a.date || '').startsWith(month)));
      setEmployees(eRes.data || []);
      setBrandsMaster(bRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [month, isManager, canSetTargets]);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 20000);
    const onFocus = () => fetchData();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(i); window.removeEventListener('focus', onFocus); };
  }, [fetchData]);

  const handleSubmitEntry = async () => {
    if (!entryDialog.achieved || !entryDialog.date) { toast.error('Fill amount and date'); return; }
    try {
      await api.post('/achievements', {
        achieved: parseFloat(entryDialog.achieved),
        date: entryDialog.date,
        product_brand: entryDialog.product_brand || '',
        notes: entryDialog.notes,
      });
      const brandLine = entryDialog.product_brand
        ? `${entryDialog.product_brand} achievement submitted — also added to Konattu total`
        : 'Non-brand sale logged in Konattu total';
      toast.success(brandLine);
      setEntryDialog({ open: false, achieved: '', date: new Date().toISOString().slice(0, 10), product_brand: '', notes: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleSetTarget = async () => {
    if (!targetDialog.employee_id || !targetDialog.target) { toast.error('Pick employee and enter target'); return; }
    try {
      await api.post('/brand-targets', {
        employee_id: targetDialog.employee_id,
        month,
        target: parseFloat(targetDialog.target),
      });
      toast.success('Target set for staff');
      setTargetDialog({ open: false, employee_id: '', target: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleApprove = async (id) => {
    try { await api.put(`/achievements/${id}/approve`); toast.success('Approved'); fetchData(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleReject = async () => {
    try {
      await api.put(`/achievements/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success('Rejected');
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDelete = async (e) => {
    if (!window.confirm(`Delete ${e.status} ${e.brand_name} entry for ${e.employee_name}?\n\nThis will also delete the linked Konattu entry.`)) return;
    try {
      await api.delete(`/achievements/${e.id}`);
      toast.success('Deleted (brand + Konattu mirror)');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const statusColor = (s) =>
    s === 'approved' ? 'bg-emerald-100 text-emerald-700' :
    s === 'rejected' ? 'bg-red-100 text-red-700' :
    'bg-amber-100 text-amber-700';

  const brandCards = (perf?.brands || []);
  const activeBrandData = selectedBrand ? brandCards.find(b => b.brand_name === selectedBrand) : null;
  const filteredEntries = selectedBrand ? entries.filter(e => e.brand_name === selectedBrand) : entries;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="achievements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {selectedBrand && (
            <Button variant="ghost" size="sm" className="h-8 rounded-sm -ml-2" onClick={() => setSelectedBrand(null)} data-testid="back-to-brands">
              <ArrowLeft className="h-4 w-4 mr-1" /> All Brands
            </Button>
          )}
          <div>
            <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">
              {selectedBrand ? selectedBrand : 'Brand Achievements'}
            </h1>
            <p className="text-neutral-500 text-sm mt-0.5">{month} · your brand is auto-linked from your profile</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="h-10 px-3 border border-neutral-200 rounded-sm text-sm bg-white" data-testid="brand-month-filter" />
          {canSubmitOwnEntry && (
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setEntryDialog(p => ({ ...p, open: true }))} data-testid="submit-brand-entry">
              <Send className="h-4 w-4 mr-2" /> Submit Today's Sale
            </Button>
          )}
          {canSetTargets && (
            <Button variant="outline" className="rounded-sm" onClick={() => setTargetDialog(p => ({ ...p, open: true }))} data-testid="set-brand-target-button">
              <Plus className="h-4 w-4 mr-2" /> Set Target
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-32 bg-neutral-100 rounded-md animate-pulse" />)}
        </div>
      ) : isManager && !selectedBrand ? (
        <>
          {/* Brand-wise performance cards */}
          {brandCards.length === 0 ? (
            <Card className="border border-neutral-200 rounded-md">
              <CardContent className="py-16 text-center text-neutral-500">
                <Trophy className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
                <p className="text-sm">No brand activity for {month}. {canSetTargets && 'Start by setting a target for a staff member.'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brandCards.map(b => (
                <Card key={b.brand_name}
                  className="border border-neutral-200 shadow-sm rounded-md hover:shadow-md hover:-translate-y-[1px] transition-all cursor-pointer"
                  onClick={() => setSelectedBrand(b.brand_name)}
                  data-testid={`brand-card-${b.brand_name}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading font-black text-neutral-900 text-lg">{b.brand_name}</h3>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400">{b.staff_count} staff</p>
                      </div>
                      <Badge className={`${b.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : b.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {b.pct}%
                      </Badge>
                    </div>
                    <div className="flex items-end justify-between pt-1">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400">Target</p>
                        <p className="text-sm font-semibold">{(b.total_target || 0).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-neutral-400">Achieved</p>
                        <p className="text-sm font-semibold text-emerald-700">{(b.total_achieved || 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden">
                      <div className={`h-full ${b.pct >= 90 ? 'bg-emerald-500' : b.pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${Math.min(b.pct, 100)}%` }} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : null}

      {/* Drill-in: staff-wise for selected brand */}
      {isManager && selectedBrand && activeBrandData && (
        <Card className="border border-neutral-200 shadow-sm rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-neutral-800">Staff · {selectedBrand}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-neutral-50">
                    <TableHead className="text-xs uppercase">Staff</TableHead>
                    <TableHead className="text-xs uppercase text-right">Target</TableHead>
                    <TableHead className="text-xs uppercase text-right">Achieved</TableHead>
                    <TableHead className="text-xs uppercase text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeBrandData.staff.map(s => (
                    <TableRow key={s.employee_id}>
                      <TableCell className="text-sm font-medium">{s.employee_name}</TableCell>
                      <TableCell className="text-sm text-right text-neutral-700">{(s.target || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right font-semibold text-emerald-700">{(s.achieved || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-right">
                        <Badge className={`${s.pct >= 90 ? 'bg-emerald-100 text-emerald-700' : s.pct >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{s.pct}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entries table — always visible under brand cards */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-neutral-800">
            {selectedBrand ? `${selectedBrand} Entries` : 'All Entries'} · {filteredEntries.length}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="text-xs uppercase">Date</TableHead>
                  <TableHead className="text-xs uppercase">Staff</TableHead>
                  <TableHead className="text-xs uppercase">Brand</TableHead>
                  <TableHead className="text-xs uppercase text-right">Target</TableHead>
                  <TableHead className="text-xs uppercase text-right">Achieved</TableHead>
                  <TableHead className="text-xs uppercase">Status</TableHead>
                  {isManager && <TableHead className="text-xs uppercase text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-neutral-500">No entries yet for {month}</TableCell></TableRow>
                ) : filteredEntries.map(e => (
                  <TableRow key={e.id} className={e.flagged ? 'bg-amber-50/40' : ''}>
                    <TableCell className="text-sm">{e.date}</TableCell>
                    <TableCell className="text-sm font-medium">{e.employee_name}</TableCell>
                    <TableCell className="text-sm">
                      <Badge variant="outline" className="text-xs">{e.brand_name}</Badge>
                      {e.flagged && <AlertTriangle className="h-3 w-3 ml-2 inline text-amber-500" title={e.flag_reason} />}
                    </TableCell>
                    <TableCell className="text-sm text-right text-neutral-700">{(e.target || 0).toLocaleString()}</TableCell>
                    <TableCell className="text-sm text-right font-semibold">{(e.achieved || 0).toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${statusColor(e.status)}`}>{e.status}</Badge>
                      {e.rejection_reason && <p className="text-[10px] text-red-500 mt-0.5">{e.rejection_reason}</p>}
                    </TableCell>
                    {isManager && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {/* Approve / Reject are now SA-only per requirement #6.
                              Managers + Management get read-only access here. */}
                          {e.status === 'pending' && isSuperAdmin && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleApprove(e.id)} data-testid={`approve-achievement-${e.id}`}>
                                <Check className="h-4 w-4 text-emerald-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRejectDialog({ open: true, id: e.id })} data-testid={`reject-achievement-${e.id}`}>
                                <X className="h-4 w-4 text-red-500" />
                              </Button>
                            </>
                          )}
                          {isSuperAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(e)} data-testid={`delete-achievement-${e.id}`}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Employee: Submit daily entry */}
      <Dialog open={entryDialog.open} onOpenChange={(o) => setEntryDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="brand-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Submit Today's Sale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-2">
              Pick the <strong>product brand</strong> sold today. Leave it empty for a non-brand (Konattu only) sale. The entry always counts toward Konattu total.
            </p>
            <div className="space-y-1">
              <Label className="text-sm">Date</Label>
              <Input type="date" value={entryDialog.date} onChange={e => setEntryDialog(p => ({ ...p, date: e.target.value }))} className="rounded-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Product Brand</Label>
              <Select value={entryDialog.product_brand || '__none__'} onValueChange={v => setEntryDialog(p => ({ ...p, product_brand: v === '__none__' ? '' : v }))}>
                <SelectTrigger className="rounded-sm" data-testid="brand-entry-product-brand"><SelectValue placeholder="Select product brand or leave for non-brand…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">— Non-brand product (Konattu only) —</SelectItem>
                  {brandsMaster.map(b => <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-neutral-400">Picking a brand updates that brand&apos;s achievement (your own brand OR another brand if cross-selling).</p>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Amount Achieved *</Label>
              <Input type="number" value={entryDialog.achieved} onChange={e => setEntryDialog(p => ({ ...p, achieved: e.target.value }))}
                placeholder="e.g., 25000" className="rounded-sm" data-testid="brand-entry-amount" />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Notes (optional)</Label>
              <Textarea value={entryDialog.notes} onChange={e => setEntryDialog(p => ({ ...p, notes: e.target.value }))} className="rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEntryDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={handleSubmitEntry} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="brand-entry-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SA/Mgmt: Set Target per staff (brand auto from profile) */}
      <Dialog open={targetDialog.open} onOpenChange={(o) => setTargetDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="set-brand-target-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Set Brand Target · {month}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-sm p-2">
              Target is set per staff for their mapped brand. Assign the staff to a brand in their Employee profile first.
            </p>
            <div className="space-y-1">
              <Label className="text-sm">Staff</Label>
              <Select value={targetDialog.employee_id} onValueChange={v => setTargetDialog(p => ({ ...p, employee_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="target-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {employees.filter(e => e.brand_classification).map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} — {e.brand_classification}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Monthly Target *</Label>
              <Input type="number" value={targetDialog.target} onChange={e => setTargetDialog(p => ({ ...p, target: e.target.value }))}
                placeholder="e.g., 100000" className="rounded-sm" data-testid="target-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setTargetDialog(p => ({ ...p, open: false }))}>Cancel</Button>
            <Button onClick={handleSetTarget} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="bt-save-button">Save Target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, id: null })}>
        <DialogContent className="max-w-sm" data-testid="achievement-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Reject Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-sm">Reason</Label>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="rounded-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectDialog({ open: false, id: null })}>Cancel</Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="achievement-reject-confirm-button">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
